import { assertNever } from "@bindery/shared/std/assert-never";
import type {
  CompositionEdit,
  CompositionToken,
  IntentJudgement,
  LadderRung,
  SightStatus,
  WordToken,
} from "./schema";
import { RESAY_CAP } from "./lineage";
import { sightOf, type SightMap } from "./sight";

export interface SurfacingThresholds {
  /** Surface for doubt below this P(word is what the child meant). */
  readonly intent: number;
  /** Surface a non-sight word whose segment's average log-probability is below this. */
  readonly segmentLogprob: number;
}

// Provisional until τ_intent and τ_seg are set from a labelled sample of K–2
// dictations (plan § 8 q5). −1 is Whisper's own low-confidence fallback
// threshold on avg_logprob (from memory, unverified).
export const PROVISIONAL_THRESHOLDS: SurfacingThresholds = {
  intent: 0.5,
  segmentLogprob: -1,
};

export interface SurfacingContext {
  readonly sight: SightMap;
  readonly thresholds: SurfacingThresholds;
  /**
   * Absent: whether a word is a dictionary word is unknown, and a word the
   * sight map cannot vouch for is doubted as `dictionary-unknown`.
   */
  readonly isDictionaryWord?: (word: string) => boolean;
}

export type DoubtSignal =
  | "low-intent"
  | "non-dictionary"
  | "dictionary-unknown"
  | "low-segment";

/**
 * Doubt about the letters themselves: no dictionary and no grown-up stands
 * behind them. Hearing settles which word the child said, never how it is
 * spelled.
 */
export const isSpellingDoubt = (signal: DoubtSignal): boolean =>
  signal === "non-dictionary" || signal === "dictionary-unknown";

const ENCODING_OPPORTUNITIES: ReadonlySet<SightStatus> = new Set([
  "emerging",
  "predicted-unknown",
  "unobserved",
]);

/**
 * Why a word is marked. Doubt (the text may not be what the child said)
 * starts the correction ladder; an encoding opportunity (the word is right
 * and not yet a sight word) only renders it tappable-to-hear.
 */
export type Surfacing =
  | {
      readonly reason: "transcription-doubt";
      readonly tokenId: string;
      readonly signals: readonly DoubtSignal[];
    }
  | {
      readonly reason: "encoding-opportunity";
      readonly tokenId: string;
      readonly status: SightStatus;
    };

const choseAnotherWord = (j: IntentJudgement): boolean =>
  j.intendedConfidence === null &&
  j.alternatives.some((a) => a.probability !== null);

const isInformative = (j: IntentJudgement): boolean =>
  j.source === "decisions-api" &&
  (j.intendedConfidence !== null || choseAnotherWord(j));

/**
 * The newest machine judgement that says something about the word. One with
 * no confidence and no alternative scored is an unanswered question, and is
 * passed over so it cannot shadow an earlier answer.
 */
export const latestMachineJudgement = (
  word: WordToken,
): IntentJudgement | null =>
  [...word.intent].reverse().find(isInformative) ?? null;

/**
 * P(the word is what the child meant) by the newest informative judgement.
 * A model that scored an alternative but not the transcribed word chose
 * another word, which is doubt: it reads as zero.
 */
export const machineConfidence = (word: WordToken): number | null => {
  const judgement = latestMachineJudgement(word);
  return judgement === null ? null : (judgement.intendedConfidence ?? 0);
};

// Recognition chose these letters. A typed, chosen or adult word is a
// dictionary or adult-confirmed spelling by construction (plan § 3).
const spelledByRecognition = (word: WordToken): boolean =>
  word.origin === "dictated" || word.origin === "re-said";

// A word with fluency evidence or a prediction came from the reader's text or
// the predictor's word list, so it is a word even with no dictionary to ask.
function dictionarySignal(
  word: WordToken,
  status: SightStatus,
  isDictionaryWord: ((word: string) => boolean) | undefined,
): DoubtSignal[] {
  if (word.properNoun && word.origin === "adult") return [];
  if (isDictionaryWord === undefined) {
    return status === "unobserved" ? ["dictionary-unknown"] : [];
  }
  return isDictionaryWord(word.text.toLowerCase()) ? [] : ["non-dictionary"];
}

const spellingSignals = (
  word: WordToken,
  context: SurfacingContext,
): DoubtSignal[] =>
  dictionarySignal(
    word,
    sightOf(context.sight, word.text).status,
    context.isDictionaryWord,
  );

function doubtSignals(
  word: WordToken,
  context: SurfacingContext,
): DoubtSignal[] {
  const spelling = spellingSignals(word, context);
  // A child's "yes" settles what the ear can, and leaves the spelling's doubt.
  if (word.resolution === "confirmed") {
    return spelledByRecognition(word) ? spelling : [];
  }
  const status = sightOf(context.sight, word.text).status;
  const confidence = machineConfidence(word);
  const logprob = word.asr?.segmentLogprob ?? null;
  return [
    ...(confidence !== null && confidence < context.thresholds.intent
      ? (["low-intent"] as const)
      : []),
    ...spelling,
    ...(logprob !== null &&
    logprob < context.thresholds.segmentLogprob &&
    status !== "sight"
      ? (["low-segment"] as const)
      : []),
  ];
}

/**
 * Whether the child may confirm this word as theirs by ear. A word only
 * recognition vouches for (a Whisper non-word, a mangled name) never is:
 * the system never keeps a misspelling as the child's text, so it goes to
 * the other rungs or to a grown-up (plan § 1, § 3, § 4).
 */
export const childCanConfirm = (
  word: WordToken,
  context: SurfacingContext,
): boolean =>
  !spelledByRecognition(word) || spellingSignals(word, context).length === 0;

/** Every marked word in document order; doubt outranks opportunity. */
export function surfaced(
  tokens: readonly CompositionToken[],
  context: SurfacingContext,
): Surfacing[] {
  return tokens.flatMap((token): Surfacing[] => {
    if (token.kind !== "word") return [];
    const signals = doubtSignals(token, context);
    if (signals.length > 0) {
      return [{ reason: "transcription-doubt", tokenId: token.id, signals }];
    }
    const status = sightOf(context.sight, token.text).status;
    return ENCODING_OPPORTUNITIES.has(status)
      ? [{ reason: "encoding-opportunity", tokenId: token.id, status }]
      : [];
  });
}

export const DEFAULT_BUILD_CAP = 3;

/**
 * Words to offer in the build exercise after a composition is finished:
 * encoding opportunities the system is surest are spelled as meant, so the
 * child never builds a wrong word. Capped, because a story must not become a
 * spelling test.
 */
/** A doubt about the letters, so the word is printed as a blank, never as the child's text. */
export const hasSpellingDoubt = (surfacing: Surfacing): boolean =>
  surfacing.reason === "transcription-doubt" &&
  surfacing.signals.some(isSpellingDoubt);

export function buildCandidates(
  tokens: readonly CompositionToken[],
  context: SurfacingContext,
  cap: number = DEFAULT_BUILD_CAP,
): string[] {
  const words = new Map(
    tokens.flatMap((t) => (t.kind === "word" ? [[t.id, t] as const] : [])),
  );
  const certainty = (word: WordToken): number =>
    word.resolution === "confirmed" ? 1 : (machineConfidence(word) ?? 0);
  return surfaced(tokens, context)
    .flatMap((s) => {
      const word = words.get(s.tokenId);
      return s.reason === "encoding-opportunity" &&
        word !== undefined &&
        certainty(word) >= context.thresholds.intent
        ? [{ id: s.tokenId, certainty: certainty(word) }]
        : [];
    })
    .sort((a, b) => b.certainty - a.certainty)
    .slice(0, cap)
    .map((c) => c.id);
}

// --- the correction ladder

const LADDER: readonly LadderRung[] = [
  "hear-and-compare",
  "choose",
  "type",
  "re-say",
  "tell-me-about-it",
  "save-for-adult",
];

export type LadderState =
  | { readonly kind: "resolved" }
  | { readonly kind: "waiting-for-adult" }
  | {
      readonly kind: "open";
      /** The rung to offer first. */
      readonly next: LadderRung;
      /** Every rung the child may take now; `save-for-adult` is always here. */
      readonly available: readonly LadderRung[];
      readonly reSays: number;
    };

const resolves = (edit: CompositionEdit, childCanConfirm: boolean): boolean =>
  (edit.op === "confirm" && childCanConfirm) ||
  edit.op === "choose" ||
  edit.op === "type" ||
  edit.op === "adult-resolve" ||
  (edit.op === "describe" && edit.result.outcome === "chosen");

export interface LadderOptions {
  readonly hasAlternatives: boolean;
  /**
   * `childCanConfirm` for the word; absent means it may. When it may not,
   * hear-and-compare is not on offer and a confirmation already in the
   * history resolves nothing.
   */
  readonly childCanConfirm?: boolean;
}

/**
 * Where a surfaced word stands on the ladder, from its history. Re-say is
 * capped at two, after which "tell me about it" takes the microphone's
 * place; a re-say that came back with the rejected word is never offered
 * again as the next step.
 */
export function nextRung(
  history: readonly CompositionEdit[],
  options: LadderOptions,
): LadderState {
  const confirmable = options.childCanConfirm ?? true;
  if (history.some((edit) => resolves(edit, confirmable))) {
    return { kind: "resolved" };
  }
  if (history.some((e) => e.op === "save-for-adult")) {
    return { kind: "waiting-for-adult" };
  }
  const reSays = history.filter((e) => e.op === "re-say");
  const rejected = new Set(
    history.flatMap((e) => (e.op === "reject" ? [e.rung] : [])),
  );
  const described = history.some((e) => e.op === "describe");
  const lastReSay = reSays[reSays.length - 1];
  const lastWasSameWord =
    lastReSay?.op === "re-say" && lastReSay.result.outcome === "same-word";

  const available = LADDER.filter((rung) => {
    switch (rung) {
      case "choose":
        return options.hasAlternatives;
      case "re-say":
        return reSays.length < RESAY_CAP;
      case "tell-me-about-it":
        return reSays.length >= RESAY_CAP && !described;
      case "hear-and-compare":
        return confirmable;
      case "type":
      case "save-for-adult":
        return true;
      default:
        return assertNever(rung, "rung");
    }
  });
  const untried = (rung: LadderRung): boolean => {
    switch (rung) {
      case "hear-and-compare":
      case "choose":
      case "type":
        return !rejected.has(rung);
      case "re-say":
        return !lastWasSameWord;
      case "tell-me-about-it":
      case "save-for-adult":
        return true;
      default:
        return assertNever(rung, "rung");
    }
  };
  const next = available.find(untried) ?? "save-for-adult";
  return { kind: "open", next, available, reSays: reSays.length };
}
